import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import React, { useCallback } from 'react';
import { View, Text } from 'react-native';
import styled from 'styled-components/native';
import { color } from '@/constant/color';
import { SccPressable } from '@/components/SccPressable';
import IcThumbsUp from '@/assets/icon/ic_thumbs_up.svg';
import IcThumbsUpFill from '@/assets/icon/ic_thumbs_up_fill.svg';
import IcShareWeb from '@/assets/icon/ic_share_web.svg';
import { useResponsive } from '../context/ResponsiveContext';
import { useEditMode } from '../context/EditModeContext';
export default function FloatingBottomBar({ title, likeCount, isLiked, ctaButtonUrl, onLikePress, onSharePress, isInApp, isSaved, isSaveDisabled, onSavePress, }) {
    var _a;
    const { isDesktop } = useResponsive();
    const editContext = useEditMode();
    const isEditMode = (_a = editContext === null || editContext === void 0 ? void 0 : editContext.isEditMode) !== null && _a !== void 0 ? _a : false;
    const handleSharePress = useCallback(() => {
        var _a;
        const shareText = `[${title}] 이동약자를 위한 진짜 리뷰를 뿌클로드에서 확인해보세요!\n${window.location.href}`;
        if (onSharePress) {
            onSharePress();
        }
        else if (typeof navigator !== 'undefined' && navigator.share) {
            navigator.share({
                title: document.title,
                text: shareText,
            }).catch(() => {
                // User cancelled or share failed
            });
        }
        else {
            // Fallback: copy to clipboard
            (_a = navigator.clipboard) === null || _a === void 0 ? void 0 : _a.writeText(shareText);
        }
    }, [onSharePress]);
    const handleCTAPress = useCallback(() => {
        if (typeof window !== 'undefined' && ctaButtonUrl) {
            window.open(ctaButtonUrl, '_blank');
        }
    }, [ctaButtonUrl]);
    const handleLikePress = useCallback(() => {
        onLikePress === null || onLikePress === void 0 ? void 0 : onLikePress();
    }, [onLikePress]);
    return (_jsxs(Container, { isDesktop: isDesktop, children: [_jsx(ButtonsSection, { isDesktop: isDesktop, children: _jsxs(ContentWrapper, { isDesktop: isDesktop, children: [_jsxs(LeftButtonsContainer, { children: [_jsx(SccPressable, { onPress: handleLikePress, elementName: "bbucle-road-floating-like", trackView: true, logParams: { likeCount, isDesktop }, disableLogging: isEditMode, 
                                    // elementName 은 로깅용이라 스크린리더에 읽히지 않는다. 아이콘+숫자만 있는
                                    // 컨트롤이므로 이름과 눌림 상태를 명시한다. (CLAUDE.md / PR #226)
                                    accessibilityRole: "button", accessibilityLabel: "\uB3C4\uC6C0\uC774 \uB3FC\uC694", accessibilityState: { selected: isLiked }, children: _jsxs(LikeButton, { isDesktop: isDesktop, children: [isLiked ? (_jsx(IcThumbsUpFill, { width: isDesktop ? 24 : 20, height: isDesktop ? 24 : 20, viewBox: "0 0 16 16" })) : (_jsx(IcThumbsUp, { width: isDesktop ? 24 : 20, height: isDesktop ? 24 : 20, viewBox: "0 0 17 16", color: "#232328" })), _jsx(LikeCount, { isDesktop: isDesktop, children: likeCount })] }) }), _jsx(SccPressable, { onPress: handleSharePress, elementName: "bbucle-road-floating-share", trackView: true, logParams: { isDesktop }, disableLogging: isEditMode, 
                                    // 아이콘 전용 버튼 — 텍스트가 없어 접근성 이름을 직접 준다. (CLAUDE.md / PR #226)
                                    accessibilityRole: "button", accessibilityLabel: "\uACF5\uC720\uD558\uAE30", children: _jsx(ShareButton, { isDesktop: isDesktop, children: _jsx(IcShareWeb, { width: isDesktop ? 24 : 20, height: isDesktop ? 24 : 20, viewBox: "0 0 24 24", color: "#16181C" }) }) })] }), _jsxs(CTAWrapper, { children: [isDesktop && !isInApp && (_jsxs(TooltipContainer, { children: [_jsx(TooltipContent, { children: _jsx(TooltipText, { children: "\uC815\uBCF4\uAC00 \uB354 \uD544\uC694\uD55C \uC7A5\uC18C\uAC00 \uC788\uB2E4\uBA74 \uC694\uCCAD\uD574\uC8FC\uC138\uC694! \uD83C\uDFAF" }) }), _jsx(TooltipArrow, {})] })), isInApp ? (_jsx(SccPressable, { onPress: onSavePress, elementName: "bbucle-road-floating-save", trackView: true, logParams: { isSaved: !!isSaved, isDesktop }, disabled: isSaveDisabled, disableLogging: isEditMode, style: { flex: 1 }, children: _jsx(CTAButton, { isDesktop: isDesktop, children: _jsx(CTAButtonText, { isDesktop: isDesktop, children: isSaved ? '저장완료' : '저장하기' }) }) })) : (_jsx(SccPressable, { onPress: handleCTAPress, elementName: "bbucle-road-floating-cta", trackView: true, logParams: { ctaButtonUrl, isDesktop }, disableLogging: isEditMode, style: { flex: 1 }, children: _jsx(CTAButton, { isDesktop: isDesktop, children: _jsx(CTAButtonText, { isDesktop: isDesktop, children: "\uC815\uBCF4 \uC694\uCCAD\uD558\uAE30!" }) }) }))] })] }) }), !isDesktop && !isInApp && (_jsx(MobileBottomBar, { children: _jsx(MobileBottomBarText, { children: "\uC815\uBCF4\uAC00 \uB354 \uD544\uC694\uD55C \uC7A5\uC18C\uAC00 \uC788\uB2E4\uBA74 \uC694\uCCAD\uD574\uC8FC\uC138\uC694! \uD83C\uDFAF" }) }))] }));
}
const Container = styled(View) `
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 200;
  background-color: ${color.white};
  border-top-width: 1px;
  border-top-color: #d8d8df;
  box-shadow: 0px -4px 20px rgba(0, 0, 0, 0.08);
`;
const ButtonsSection = styled(View) `
  padding-top: ${({ isDesktop }) => (isDesktop ? '16px' : '10px')};
  padding-bottom: ${({ isDesktop }) => (isDesktop ? '20px' : '20px')};
  padding-horizontal: ${({ isDesktop }) => (isDesktop ? '24px' : '16px')};
`;
const ContentWrapper = styled(View) `
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  max-width: 1020px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
`;
const LeftButtonsContainer = styled(View) `
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-right: 8px;
`;
const LikeButton = styled(View) `
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${({ isDesktop }) => (isDesktop ? '7px' : '6px')};
  height: ${({ isDesktop }) => (isDesktop ? '60px' : '48px')};
  padding-horizontal: ${({ isDesktop }) => (isDesktop ? '16px' : '10px')};
  padding-vertical: 4px;
  border-radius: 4px;
  border-width: 1px;
  border-color: #d8d8df;
`;
const LikeCount = styled(Text) `
  font-family: Pretendard;
  font-size: ${({ isDesktop }) => (isDesktop ? '20px' : '16px')};
  line-height: 30px;
  font-weight: 500;
  color: #24262b;
`;
const ShareButton = styled(View) `
  align-items: center;
  justify-content: center;
  width: ${({ isDesktop }) => (isDesktop ? '60px' : '48px')};
  height: ${({ isDesktop }) => (isDesktop ? '60px' : '48px')};
  border-radius: 4px;
  border-width: 1px;
  border-color: #d8d8df;
  background-color: ${color.white};
`;
const CTAButton = styled(View) `
  width: 100%;
  height: ${({ isDesktop }) => (isDesktop ? '60px' : '48px')};
  padding-horizontal: ${({ isDesktop }) => (isDesktop ? '32px' : '20px')};
  padding-vertical: 12px;
  border-radius: 4px;
  background-color: #16181c;
  align-items: center;
  justify-content: center;
`;
const CTAButtonText = styled(Text) `
  font-family: Pretendard;
  font-size: ${({ isDesktop }) => (isDesktop ? '18px' : '15px')};
  line-height: ${({ isDesktop }) => (isDesktop ? '26px' : '22px')};
  font-weight: 700;
  letter-spacing: -0.36px;
  color: #b8ff55;
`;
const CTAWrapper = styled(View) `
  flex: 1;
  position: relative;
`;
const TooltipContainer = styled(View) `
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  margin-bottom: 5px;
  align-items: center;
  filter: drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.25)) drop-shadow(0px 2px 8px rgba(0, 0, 0, 0.2));
`;
const TooltipContent = styled(View) `
  background-color: #b8ff55;
  padding-horizontal: 20px;
  padding-vertical: 6px;
  border-radius: 4px;
`;
const TooltipText = styled(Text) `
  font-family: Pretendard;
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  letter-spacing: -0.28px;
  color: #000000;
`;
const TooltipArrow = styled(View) `
  width: 0;
  height: 0;
  border-left-width: 8px;
  border-left-color: transparent;
  border-right-width: 8px;
  border-right-color: transparent;
  border-top-width: 8px;
  border-top-color: #b8ff55;
`;
const MobileBottomBar = styled(View) `
  background-color: #b8ff55;
  height: 31px;
  width: 100%;
  align-items: center;
  justify-content: center;
  padding-horizontal: 20px;
`;
const MobileBottomBarText = styled(Text) `
  font-family: Pretendard;
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  letter-spacing: -0.28px;
  color: #000000;
`;
